import { create } from 'zustand';

import { getTimeCost } from '@/shared/lib/getTimeCost';
import { getPlanName, getPlanPeriodLabel } from '@/shared/lib/subscription';
import { PaymentInterval, Products } from '@/shared/types/Billing';
import { Project } from '@/shared/types/Project';

type SubscriptionData = {
  hasActiveSubscription?: boolean;
  hasTrialPlan?: boolean;
  subscription?: string;
};

export type ProjectData = {
  dictionary: string;
  dstLang: Project['dst_lang'];
  durationMin: number | string;
  isSrtUploaded: boolean;
  locProjectId: Project['id'];
  raskTTS: boolean;
  srcLang: Project['src_lang'];
};

type State = {
  projectData?: ProjectData;
  subscriptionData?: SubscriptionData;
};

type SetMixpanelSubscriptionData = {
  hasActiveSubscription: boolean;
  hasFreeSubscription: boolean;
  hasTrialPlan: boolean;
  interval?: PaymentInterval;
  productId?: string;
  products?: Products;
};

type SetMixpanelProjectData = {
  project: Project;
};

type Actions = {
  resetMixpanelProjectData: () => void;
  resetMixpanelProps: () => void;
  setMixpanelProjectData: (data: SetMixpanelProjectData) => void;
  setMixpanelSubscriptionData: (data: SetMixpanelSubscriptionData) => void;
};

const initialState: State = {};

export const useMixpanelPropsStore = create<Actions & State>((set) => ({
  ...initialState,
  resetMixpanelProjectData: () => set((state) => ({ ...state, projectData: undefined })),
  resetMixpanelProps: () => set(initialState),
  setMixpanelProjectData: ({ project }) => {
    set((state) => ({
      ...state,
      projectData: {
        dictionary: project.glossary_id ? 'on' : 'off',
        dstLang: project.dst_lang,
        durationMin: project.duration ? getTimeCost({ duration: project.duration }) : 'NA',
        isSrtUploaded: project.src_srt_uploaded || project.dst_srt_uploaded,
        locProjectId: project.id,
        provider: project.tts_provider || 'NA',
        raskTTS: project.used_rask_tts || project.free_dubbing,
        srcLang: project.src_lang,
      },
    }));
  },
  setMixpanelSubscriptionData: ({
    hasActiveSubscription,
    hasFreeSubscription,
    hasTrialPlan,
    interval,
    productId,
    products,
  }) => {
    const planName = getPlanName({
      hasFreeSubscription,
      hasTrialPlan,
      productId,
      products,
    });
    const periodLabel = getPlanPeriodLabel(interval);

    set((state) => ({
      ...state,
      subscriptionData: {
        hasActiveSubscription,
        hasTrialPlan,
        ...(hasActiveSubscription && { subscription: `${planName} ${periodLabel}` }),
      },
    }));
  },
}));

export const resetMixpanelProps = () => useMixpanelPropsStore.getState().resetMixpanelProps();
